import { isMissing, routing, type Route } from '@/api/proposed'
import type { MapStation } from '@/api/types'
import { distanceKm } from './geo'

/** Public OSRM demo; replace with the API's /public/route (D15) or a hosted router for production use. */
export const OSRM = import.meta.env.VITE_ROUTER_URL ?? 'https://router.project-osrm.org'

export async function getRoute(from: [number, number], to: [number, number]): Promise<Route> {
  try {
    return await routing.route(from, to)
  } catch (e) {
    if (!isMissing(e)) throw e
  }
  const res = await fetch(`${OSRM}/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`)
  if (!res.ok) throw new Error('Could not find a road route. Try again in a moment.')
  const json = (await res.json()) as { code: string; routes: { distance: number; duration: number; geometry: { coordinates: [number, number][] } }[] }
  const r = json.routes?.[0]
  if (json.code !== 'Ok' || !r) throw new Error('No road route between these places.')
  return { distance_km: r.distance / 1000, duration_min: r.duration / 60, geometry: r.geometry.coordinates.map(([lng, lat]) => [lat, lng]) }
}

export interface AlongStation {
  station: MapStation
  /** km from the start, measured along the road to the nearest point. */
  along: number
  /** km off the road. */
  off: number
  dc: boolean
  maxKw: number
}

/** Stations within `corridorKm` of the route, ordered from the start. */
export function stationsAlong(route: Route, stations: MapStation[], corridorKm = 5): AlongStation[] {
  const pts = route.geometry
  const cum = [0]
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + distanceKm({ lat: pts[i - 1][0], lng: pts[i - 1][1] }, { lat: pts[i][0], lng: pts[i][1] }))
  // The router's distance is authoritative; scale the measured polyline to it.
  const scale = cum[cum.length - 1] ? route.distance_km / cum[cum.length - 1] : 1
  const out: AlongStation[] = []
  for (const s of stations) {
    if (s.latitude == null || s.longitude == null) continue
    let best = Infinity
    let at = 0
    for (let i = 0; i < pts.length; i++) {
      const d = distanceKm({ lat: s.latitude, lng: s.longitude }, { lat: pts[i][0], lng: pts[i][1] })
      if (d < best) {
        best = d
        at = i
      }
    }
    if (best <= corridorKm) {
      const maxKw = Math.max(0, ...s.chargers.filter((c) => c.status !== 'Offline').map((c) => c.max_power_kw ?? 0))
      out.push({ station: s, along: cum[at] * scale, off: best, dc: s.chargers.some((c) => c.charger_type === 'DC' && c.status !== 'Offline'), maxKw })
    }
  }
  return out.sort((a, b) => a.along - b.along)
}

export interface Plan {
  stops: (AlongStation & { arrivePct: number; leavePct: number; minutes: number })[]
  arrivePct: number
  /** Where the car would run below the reserve with no station in reach. */
  gapAtKm: number | null
}

/**
 * Greedy stops: drive as far as the battery allows above the reserve, stop at
 * the furthest station in reach (fast chargers first), charge to `targetPct`.
 */
export function planStops(total: number, along: AlongStation[], o: { rangeKm: number; batteryKwh: number; startPct: number; reservePct: number; targetPct: number }): Plan {
  const stops: Plan['stops'] = []
  let pos = 0
  let pct = o.startPct
  const kmFor = (p: number) => (o.rangeKm * p) / 100
  for (let guard = 0; guard < 20; guard++) {
    if (pos + kmFor(pct - o.reservePct) >= total) break
    const reach = pos + kmFor(pct - o.reservePct)
    const inReach = along.filter((a) => a.along > pos + 1 && a.along <= reach)
    if (!inReach.length) return { stops, arrivePct: pct - ((total - pos) / o.rangeKm) * 100, gapAtKm: Math.round(reach) }
    const dc = inReach.filter((a) => a.dc)
    const pick = (dc.length ? dc : inReach).reduce((a, b) => (b.along > a.along ? b : a))
    const arrive = pct - ((pick.along - pos) / o.rangeKm) * 100
    const leave = Math.max(arrive, o.targetPct)
    const kwhAdded = ((leave - arrive) / 100) * o.batteryKwh
    // Cars rarely take full power; assume 70% of the charger's rating, at most 60 kW on average.
    const avgKw = Math.min(60, Math.max(3, pick.maxKw * 0.7))
    stops.push({ ...pick, arrivePct: arrive, leavePct: leave, minutes: Math.round((kwhAdded / avgKw) * 60) })
    pos = pick.along
    pct = leave
  }
  return { stops, arrivePct: pct - ((total - pos) / o.rangeKm) * 100, gapAtKm: null }
}
